import { useMemo } from "react";
import {
  Box,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { TableSort } from "../../../../../features/optifundDashboardSlice";
import { formatCurrency, formatInteger } from "../formatters";
import { BrandMtdRow } from "./brandsApiMapping";

const TABLE_HEADERS = [
  { key: "brand", label: "Brand", align: "left" as const },
  { key: "reported", label: "Reported", align: "right" as const },
  { key: "paid", label: "Paid", align: "right" as const },
  { key: "income", label: "Total income", align: "right" as const },
  { key: "epq", label: "EPQ", align: "right" as const },
  { key: "epp", label: "EPP", align: "right" as const },
  { key: "pending", label: "Pending", align: "right" as const },
  { key: "initial", label: "Initial", align: "right" as const },
];

interface BrandsMtdTableProps {
  rows: BrandMtdRow[];
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  sort: TableSort;
  onSortChange: (sort: TableSort) => void;
}

export default function BrandsMtdTable({
  rows,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  sort,
  onSortChange,
}: BrandsMtdTableProps) {
  const sortedRows = useMemo(() => {
    const { keytoSort, direction } = sort;
    return rows.slice().sort((a: any, b: any) => {
      const aValue = a[keytoSort];
      const bValue = b[keytoSort];
      if (typeof aValue === "string" && typeof bValue === "string") {
        return direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    });
  }, [rows, sort]);

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (key: string) => {
    onSortChange({
      keytoSort: key,
      direction: sort.keytoSort === key && sort.direction === "asc" ? "desc" : "asc",
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(+event.target.value);
  };

  return (
    <Box>
      <Typography sx={{ mb: 1.5, fontSize: 16, fontWeight: 500, color: "#1a1a1a" }}>
        Brands Performance - MTD
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#ececec" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  align={header.align}
                  sx={{ fontWeight: 700, color: "#8E24AA", backgroundColor: "#faf8fb", fontSize: 12 }}
                >
                  <TableSortLabel
                    active={sort.keytoSort === header.key}
                    direction={sort.keytoSort === header.key ? sort.direction : "asc"}
                    onClick={() => handleSort(header.key)}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row) => (
              <TableRow key={row.brand}>
                <TableCell sx={{ fontWeight: 500, fontSize: 13.5, color: "#212121" }}>{row.brand}</TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.reported)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.paid)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.income)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.epq)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatCurrency(row.epp)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.pending)}
                </TableCell>
                <TableCell align="right" sx={{ color: "#616161", fontSize: 13.5 }}>
                  {formatInteger(row.initial)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="center">
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-selectLabel": { display: "none" },
            ".MuiTablePagination-displayedRows": { display: "none" },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />
        <Pagination
          count={Math.max(1, Math.ceil(sortedRows.length / rowsPerPage))}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
          page={page + 1}
          onChange={(_event, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-displayedRows": { display: "none" },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginRight: "15px" },
          }}
        />
      </Stack>
    </Box>
  );
}
